/**
 * DealCard (SPEC E3 - pipeline-and-capi, R2).
 *
 * A single opportunity card in a kanban column. Shows contact name, company,
 * deal value (formatted), and a rotting/stale visual badge (E3-H1) when the
 * deal's days-in-stage exceeds the stage's rotting_days. Draggable via
 * @dnd-kit/core useDraggable. Clicking opens the deal-detail side panel.
 */

import { useDraggable } from '@dnd-kit/core';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { Opportunity } from '@/types/pipeline';

export function formatValue(
  value?: number | null,
  currency = 'CAD'
): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return ' - ';
  }
  return `$${Number(value).toLocaleString('en-CA', {
    maximumFractionDigits: 0,
  })} ${currency}`;
}

/** Days since the opportunity last changed stage. */
export function daysInStage(opp: Opportunity, now = new Date()): number | null {
  const ts = opp.stage_changed_at || opp.updated_at || opp.created_at;
  if (!ts) return null;
  const then = new Date(ts);
  if (Number.isNaN(then.getTime())) return null;
  return (now.getTime() - then.getTime()) / 86_400_000;
}

export function isRotting(
  opp: Opportunity,
  rottingDays?: number | null,
  now = new Date()
): boolean {
  if (!rottingDays || rottingDays <= 0) return false;
  const dis = daysInStage(opp, now);
  if (dis === null) return false;
  return dis > rottingDays;
}

interface DealCardProps {
  opportunity: Opportunity;
  rottingDays?: number | null;
  currency?: string;
  onClick?: (opp: Opportunity) => void;
  // R3c multi-select: optional select checkbox + selected state.
  selected?: boolean;
  onSelectChange?: (id: string, checked: boolean) => void;
}

export function DealCard({
  opportunity,
  rottingDays,
  currency = 'CAD',
  onClick,
  selected,
  onSelectChange,
}: DealCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: opportunity.id, data: { opportunity } });

  const rotting = isRotting(opportunity, rottingDays);
  const dis = daysInStage(opportunity);

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const name =
    opportunity.contact_name ||
    (opportunity.contact_id ? `Contact ${opportunity.contact_id.slice(0, 8)}` : 'Unknown contact');

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      role="button"
      aria-label={`Deal ${name}`}
      onClick={() => onClick?.(opportunity)}
      data-testid={`deal-card-${opportunity.id}`}
      className={cn(
        'p-3 mb-2 cursor-grab active:cursor-grabbing select-none space-y-1 border',
        isDragging && 'opacity-50',
        rotting && 'border-amber-500/70 bg-amber-50/40'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {onSelectChange && (
            // stop pointer + click so selecting does not start a drag or open the card.
            <div
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={selected}
                onCheckedChange={(v) => onSelectChange(opportunity.id, v === true)}
                aria-label={`Select deal ${name}`}
              />
            </div>
          )}
          <span className="text-sm font-medium truncate">{name}</span>
        </div>
        {rotting && (
          <Badge
            variant="outline"
            className="border-amber-500 text-amber-700 shrink-0"
            title={dis !== null ? `${Math.floor(dis)} days in stage` : 'Rotting'}
            data-testid={`rotting-badge-${opportunity.id}`}
          >
            Rotting
          </Badge>
        )}
      </div>
      {opportunity.company_name && (
        <p className="text-xs text-muted-foreground truncate">
          {opportunity.company_name}
        </p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">
          {formatValue(opportunity.value, opportunity.currency || currency)}
        </span>
        {dis !== null && (
          <span className="text-[11px] text-muted-foreground">
            {Math.floor(dis)}d in stage
          </span>
        )}
      </div>
    </Card>
  );
}
